<template>
  <span
    class="tt-wrap"
    :class="{ 'tt-block': block, 'tt-has-content': hasContent, [`tt-cursor-${cursor}`]: hasContent }"
    :tabindex="hasContent ? 0 : -1"
    @mouseenter="positionBubble"
    @focusin="positionBubble"
  >
    <slot />
    <span
      v-if="hasContent"
      ref="bubbleRef"
      class="tt-bubble"
      :class="[`tt-${placement}`, `tt-align-${align}`]"
      :style="{ maxWidth: maxWidth + 'px', '--tt-nudge': nudge + 'px' }"
      role="tooltip"
    >
      <slot name="content">{{ text }}</slot>
    </span>
  </span>
</template>

<script setup>
// Reusable CSS hover-tooltip — replaces the native HTML `title=`
// attribute across the platform.
//
// Why: native `title=` is unreliable (no tooltip shown in some
// browsers / setups, no styling control, can't include rich content)
// AND inconsistent — some users see it, some don't. The CSS bubble
// always renders and is consistent.
//
// Usage:
//   <Tooltip text="Explanation here">
//     <span class="my-trigger">€1,234</span>
//   </Tooltip>
//
// Or with rich content via slot:
//   <Tooltip>
//     <span class="my-trigger">€1,234</span>
//     <template #content>
//       <strong>Title</strong><br/>
//       More text here…
//     </template>
//   </Tooltip>
//
// Props:
//   text        — plain-text tooltip content (alternative to slot)
//   placement   — 'top' (default) | 'bottom' | 'left' | 'right'
//                 — which SIDE of the trigger the bubble sits on
//   align       — 'center' (default) | 'start' | 'end'
//                 — for top/bottom placement: how the bubble is
//                   horizontally aligned to the trigger.
//                   center = bubble center over trigger center
//                   start  = bubble left edge over trigger left edge
//                            (use this when the trigger is near the
//                            LEFT edge of its container so the bubble
//                            extends rightward — into available space)
//                   end    = bubble right edge over trigger right edge
//                            (use near the RIGHT edge)
//                 — for left/right placement: vertical alignment with
//                   the same semantics (start=top edge, end=bottom edge)
//   maxWidth    — number in px (default 320). Bubble wraps text.
//   block       — when true, wrapper renders as block instead of
//                 inline. Useful when wrapping block elements like
//                 <div> or a full row.
//
// Accessibility:
//   - tabindex=0 makes the wrapper keyboard-focusable so the
//     tooltip appears on Tab focus, not just mouse hover.
//   - role="tooltip" on the bubble for screen readers.
//
// Limitations (acceptable for now):
//   - No auto-flip near viewport edges. If a tooltip clips, override
//     the placement prop at that call site (e.g. placement="bottom").
//   - Tooltip stays inside the wrapper element. For elements with
//     overflow:hidden ancestors, the bubble may be clipped — in
//     those cases the call site needs to either lift the overflow
//     OR move the trigger outside the clipping container.
import { computed, useSlots, ref } from 'vue'

const props = defineProps({
  text:      { type: String, default: '' },
  placement: { type: String, default: 'top' },
  align:     { type: String, default: 'center' },
  maxWidth:  { type: Number, default: 320 },
  block:     { type: Boolean, default: false },
  // Cursor shown over the trigger. 'help' (default) suits an inert value or
  // label whose only purpose is to explain itself. Use 'pointer' when the
  // trigger is an actionable control (button/link) so it reads as clickable.
  cursor:    { type: String, default: 'help' },
})

const slots = useSlots()
const hasContent = computed(() => !!(props.text || slots.content))

// Auto-nudge a top/bottom bubble back on-screen when it would overflow the
// viewport horizontally (it's otherwise clipped by the page's overflow-x:clip).
// Measured on show; `--tt-nudge` is added to the bubble's transform in CSS.
const bubbleRef = ref(null)
const nudge = ref(0)
function positionBubble() {
  const b = bubbleRef.value
  if (!b || (props.placement !== 'top' && props.placement !== 'bottom')) return
  const prev = nudge.value
  const r = b.getBoundingClientRect()
  const m = 8 // keep this much gap from the boundary

  // Boundary = the nearest ancestor that actually clips (e.g. .content with
  // overflow-x:clip, whose LEFT edge is the sidebar — not viewport-left),
  // clamped to the viewport. Using the real clip box stops the bubble being
  // cut at the content edge even after nudging.
  let lo = 0, hi = window.innerWidth
  for (let n = b.parentElement; n; n = n.parentElement) {
    const ox = getComputedStyle(n).overflowX
    if (ox && ox !== 'visible') {
      const cr = n.getBoundingClientRect()
      lo = Math.max(lo, cr.left)
      hi = Math.min(hi, cr.right)
      break
    }
  }

  const natRight = r.right - prev   // edges with the current nudge removed
  const natLeft  = r.left  - prev
  let dx = 0
  if (natRight > hi - m)     dx = (hi - m) - natRight   // overflowing right edge
  else if (natLeft < lo + m) dx = (lo + m) - natLeft    // overflowing left edge
  nudge.value = Math.round(dx)
}
</script>

<style scoped>
.tt-wrap {
  position: relative;
  display: inline-block;
  outline: none;
}
.tt-wrap.tt-block {
  display: block;
}
/* The help cursor + the descendant override are gated on
   `.tt-has-content` — when the bound `text` is empty (or no content
   slot), the wrapper is just an inert pass-through and MUST NOT
   show a question-mark cursor. Without this gate, callers that
   conditionally render tooltip text (e.g. `:text="i > 0 ? '...' : ''"`)
   would mislead users into hovering an element that never shows
   a bubble. */
/* Cursor over the trigger follows the `cursor` prop. 'help' (default) forces the
   question-mark on the trigger and its children (an inert value explaining itself);
   'pointer' leaves an actionable control reading as clickable. */
.tt-wrap.tt-cursor-help {
  cursor: help;
}
.tt-wrap.tt-cursor-help :deep(*) {
  cursor: help;
}
.tt-wrap.tt-cursor-pointer {
  cursor: pointer;
}
.tt-wrap.tt-cursor-default {
  cursor: default;
}
/* Mild focus ring so keyboard users see the trigger when tabbing
   through the page. Doesn't apply to mouse hover (no ring then). */
.tt-wrap:focus-visible {
  outline: 2px solid #4F7EB3;
  outline-offset: 2px;
  border-radius: 4px;
}

.tt-bubble {
  position: absolute;
  z-index: 50;
  width: max-content;
  padding: 8px 11px;
  background: #1a2a36;
  color: #fff;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 1.45;
  text-align: left;
  border-radius: 8px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.12s ease-out;
  white-space: normal;
}

/* Reveal on hover OR keyboard focus. Hover waits a short beat before showing
   (so passing the pointer over a control doesn't flash a bubble); it hides
   immediately (base rule has no delay). Keyboard focus shows instantly. */
.tt-wrap:hover > .tt-bubble {
  opacity: 1;
  transition-delay: 0.35s;
}
.tt-wrap:focus-within > .tt-bubble {
  opacity: 1;
  transition-delay: 0s;
}

/* ---------- placement variants (top / bottom — horizontal axis) ----------
   Default alignment is `center`; .tt-align-start / .tt-align-end below
   override the horizontal positioning so the bubble doesn't clip when
   the trigger is near the left / right edge of its container. */
.tt-bubble.tt-top {
  bottom: calc(100% + 8px);
}
.tt-bubble.tt-bottom {
  top: calc(100% + 8px);
}
.tt-bubble.tt-top.tt-align-center,
.tt-bubble.tt-bottom.tt-align-center {
  left: 50%;
  transform: translateX(calc(-50% + var(--tt-nudge, 0px)));
}
.tt-bubble.tt-top.tt-align-start,
.tt-bubble.tt-bottom.tt-align-start {
  left: 0;
  transform: translateX(var(--tt-nudge, 0px));
}
.tt-bubble.tt-top.tt-align-end,
.tt-bubble.tt-bottom.tt-align-end {
  right: 0;
  transform: translateX(var(--tt-nudge, 0px));
}

/* ---------- placement variants (left / right — vertical axis) ----------
   Default alignment is `center`; start = top edge aligns, end = bottom. */
.tt-bubble.tt-left {
  right: calc(100% + 8px);
}
.tt-bubble.tt-right {
  left: calc(100% + 8px);
}
.tt-bubble.tt-left.tt-align-center,
.tt-bubble.tt-right.tt-align-center {
  top: 50%;
  transform: translateY(-50%);
}
.tt-bubble.tt-left.tt-align-start,
.tt-bubble.tt-right.tt-align-start {
  top: 0;
}
.tt-bubble.tt-left.tt-align-end,
.tt-bubble.tt-right.tt-align-end {
  bottom: 0;
}

/* ---------- arrows ---------- */
.tt-bubble::after {
  content: '';
  position: absolute;
  border: 6px solid transparent;
}
/* Top/bottom arrows — horizontal position follows the alignment so the
   arrow points to roughly where the trigger is. */
.tt-bubble.tt-top::after  { top: 100%; border-top-color: #1a2a36; }
.tt-bubble.tt-bottom::after { bottom: 100%; border-bottom-color: #1a2a36; }
.tt-bubble.tt-top.tt-align-center::after,
.tt-bubble.tt-bottom.tt-align-center::after {
  left: 50%;
  /* counter the bubble's nudge so the arrow keeps pointing at the trigger */
  transform: translateX(calc(-50% - var(--tt-nudge, 0px)));
}
.tt-bubble.tt-top.tt-align-start::after,
.tt-bubble.tt-bottom.tt-align-start::after {
  left: 14px;
}
.tt-bubble.tt-top.tt-align-end::after,
.tt-bubble.tt-bottom.tt-align-end::after {
  right: 14px;
}

/* Left/right arrows */
.tt-bubble.tt-left::after  { left: 100%; border-left-color: #1a2a36; }
.tt-bubble.tt-right::after { right: 100%; border-right-color: #1a2a36; }
.tt-bubble.tt-left.tt-align-center::after,
.tt-bubble.tt-right.tt-align-center::after {
  top: 50%;
  transform: translateY(-50%);
}
.tt-bubble.tt-left.tt-align-start::after,
.tt-bubble.tt-right.tt-align-start::after {
  top: 14px;
}
.tt-bubble.tt-left.tt-align-end::after,
.tt-bubble.tt-right.tt-align-end::after {
  bottom: 14px;
}
</style>
